import { weekFileName } from '../../lib/weekFileName';
import { DocLangToggle } from '../ui/doc-lang-toggle';
import { useTranslation } from 'react-i18next';
import { WeekFilter } from '../ui/week-filter';
import { matchesWeek } from '../../lib/weekFilter';
import i18n from '../../i18n';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import {
  Plus, Search, ClipboardCheck, Filter, Trash2, FileText,
  CheckCircle, AlertCircle, Clock, Zap, Wrench, ChevronDown, ChevronUp, Download, Printer, UserCog, Camera
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import ChecklistFillForm from './ChecklistFillForm';
import LazyAuthImage from '../shared/LazyAuthImage';
import ReassignOwnerDialog from './ReassignOwnerDialog';
import usePermissions from '../../hooks/usePermissions';
import useDeepShortcut from '../../hooks/useDeepShortcut';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import {
  cacheChecklist, getCachedChecklist, listCachedChecklists, isLocalId,
  LOCAL_ID_PREFIX, cacheTemplates, getCachedTemplates,
} from '../../lib/offlineQueue';
import { loadSession, patchSession } from '../../lib/sessionState';
import { DateText } from '../ui/week-badge';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_CONFIG = {
  draft: { label: i18n.t('auto.lbl.draft'), color: 'bg-gray-100 text-gray-700', icon: Clock },
  in_progress: { label: i18n.t('auto.lbl.inProgress'), color: 'bg-blue-100 text-blue-700', icon: AlertCircle },
  completed: { label: i18n.t('auto.lbl.approved'), color: 'bg-green-100 text-green-700', icon: CheckCircle },
  conditional_approval: { label: i18n.t('auto.lbl.conditionalApproval'), color: 'bg-orange-100 text-orange-700', icon: AlertCircle },
  signed_off: { label: i18n.t('auto.lbl.signedOff'), color: 'bg-green-100 text-green-700', icon: CheckCircle },
};

const DISCIPLINE_ICON = {
  electrical: Zap,
  mechanical: Wrench,
};

const InstallationChecklists = ({ token, projects = [], employees = [], onRefresh, currentUser }) => {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const isOnline = useOnlineStatus();
  const [templates, setTemplates] = useState([]);
  const [instances, setInstances] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [weekFilter, setWeekFilter] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDiscipline, setFilterDiscipline] = useState('all');
  const [filterOwner, setFilterOwner] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');
  const [groupByType, setGroupByType] = useState(false);
  const [reassignTarget, setReassignTarget] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [showFillForm, setShowFillForm] = useState(false);
  const [headerData, setHeaderData] = useState({});
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Dashboard shortcuts: pin the open ISO sheet, or the New sheet picker.
  useDeepShortcut({
    active: showFillForm && !!selectedInstance,
    tab: 'iso9001',
    context: { isoSubTab: 'checklists', focusIsoSheetId: selectedInstance?.id },
    label: selectedInstance?.checklist_number || selectedInstance?.template_name,
    subtitle: 'ISO 9001',
    icon: 'checklists',
    perms: ['iso_checklists.view'],
    priority: 3,
    focusKey: 'focusIsoSheetId',
    onFocus: (id) => handleOpenInstance(id),
  });
  useDeepShortcut({
    active: showCreateDialog,
    tab: 'iso9001',
    context: { isoSubTab: 'checklists', create: 'iso_checklist' },
    label: t('auto.lbl.newIsoSheet'),
    subtitle: 'ISO 9001',
    icon: 'checklists',
    perms: ['iso_checklists.view'],
    priority: 4,
    focusKey: 'create',
    onFocus: (v) => { if (v === 'iso_checklist') setShowCreateDialog(true); },
  });

  const fetchTemplates = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/checklists/templates`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('fetch failed');
      const data = await resp.json();
      setTemplates(data);
      // Templates are what make a sheet fillable with no signal.
      cacheTemplates(data);
    } catch (e) {
      setTemplates(getCachedTemplates());
    }
  }, [token]);

  // When the user picks a project in the create dialog, preview the
  // auto-fill values (project name, client from customer, supervisor) so the
  // user sees them and can edit before submitting. The same resolution runs
  // again on the backend, so any field already typed by the user is kept.
  const fetchProjectAutoFill = useCallback(async (projectId, discipline) => {
    if (!projectId) return;
    try {
      const proj = projects.find(p => p.id === projectId);
      if (!proj) return;
      let clientName = '';
      if (proj.customer_id) {
        const cResp = await fetch(`${API_BASE}/api/customers/${proj.customer_id}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (cResp.ok) {
          const c = await cResp.json();
          clientName = c.name || c.company_name || '';
        }
      }
      const supervisorKey = discipline === 'electrical' ? 'electrical_supervisor' : 'mechanical_supervisor';
      const supName = proj[supervisorKey]?.name || '';
      setHeaderData(prev => ({
        project_name: prev.project_name || proj.name || '',
        client: prev.client || clientName,
        [supervisorKey]: prev[supervisorKey] || supName,
        ...prev, // user-typed values win
      }));
    } catch {}
  }, [projects, token]);

  const fetchInstances = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/checklists/instances`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('fetch failed');
      const server = await resp.json();
      // Sheets started with no signal live only on the device until the
      // queue drains, so show them alongside the server's.
      const cached = await listCachedChecklists().catch(() => []);
      const localOnly = cached
        .filter((c) => isLocalId(c.id))
        .map((c) => ({ ...c.instance, _offline: true }));
      setInstances([...localOnly, ...server]);
    } catch (e) {
      // No signal — work entirely from what the device already holds.
      const cached = await listCachedChecklists().catch(() => []);
      setInstances(cached.map((c) => ({ ...c.instance, _offline: isLocalId(c.id), _cached: true })));
    }
  }, [token]);

  /**
   * Keep the last 30 days of sheets on the device so a daily inspection can
   * be opened and filled in a yard with no coverage. Runs once both the
   * sheets and their templates are in hand — they load in parallel, and a
   * sheet without its template is not fillable offline.
   *
   * `primedRef` is what stops a feedback loop: each write notifies the
   * offline queue, the queue listener refetches, and without the guard the
   * new list would be primed again, forever.
   */
  const primedRef = useRef(new Set());
  useEffect(() => {
    const tpls = templates.length ? templates : getCachedTemplates();
    if (!tpls.length || !instances.length) return;
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    instances.forEach((inst) => {
      if (inst._offline || inst._cached || isLocalId(inst.id)) return;
      const stamp = Date.parse(inst.updated_at || inst.created_at || '') || 0;
      if (stamp < cutoff) return;
      const mark = `${inst.id}:${stamp}`;
      if (primedRef.current.has(mark)) return;
      const tpl = tpls.find((t) => t.id === inst.template_id);
      if (!tpl) return;
      primedRef.current.add(mark);
      cacheChecklist(inst, tpl).catch(() => { /* storage full — not fatal */ });
    });
  }, [instances, templates]);

  useEffect(() => {
    fetchTemplates();
    fetchInstances();
  }, [fetchTemplates, fetchInstances]);

  const handleCreateInstance = async () => {
    if (!selectedTemplate) { toast.error(t('auto.iso.selectATemplate')); return; }
    setIsLoading(true);
    try {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        await createOffline();
        return;
      }
      const resp = await fetch(`${API_BASE}/api/checklists/instances`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          template_id: selectedTemplate.id,
          project_id: selectedProjectId || null,
          header_data: headerData,
        }),
      });
      if (!resp.ok) throw new Error((await resp.json()).detail || 'Failed to create');
      const data = await resp.json();
      toast.success(t('auto.iso.checklistCreated'));
      setShowCreateDialog(false);
      setHeaderData({});
      setSelectedTemplate(null);
      setSelectedProjectId('');
      fetchInstances();
      // Open the newly created instance for filling
      const instResp = await fetch(`${API_BASE}/api/checklists/instances/${data.id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (instResp.ok) {
        setSelectedInstance(await instResp.json());
        setShowFillForm(true);
      }
    } catch (e) { toast.error(e.message); }
    finally { setIsLoading(false); }
  };

  /**
   * Start a sheet with no signal. It carries a temporary number so the
   * driver knows which is which; the server issues the real CL-xxxx when
   * the queue drains.
   */
  const createOffline = async () => {
    const template = templates.find((t) => t.id === selectedTemplate.id)
      || getCachedTemplates().find((t) => t.id === selectedTemplate.id);
    if (!template) {
      toast.error(t('auto.iso.thatTemplateIsNotOnThis'));
      return;
    }
    const localId = `${LOCAL_ID_PREFIX}${crypto.randomUUID()}`;
    const instance = {
      id: localId,
      checklist_number: 'CL-PENDING',
      template_id: template.id,
      template_name: template.name,
      doc_ref: template.doc_ref,
      discipline: template.discipline,
      project_id: selectedProjectId || null,
      status: 'draft',
      header_data: headerData || {},
      sections_data: (template.sections || []).map((s) => ({
        items: (s.items || []).map(() => ({ status: '', value: '', notes: '' })),
      })),
      sign_offs: (template.sign_off_roles || []).map((r) => ({ ...r, signed: false })),
      created_at: new Date().toISOString(),
      created_by_name: currentUser?.full_name || currentUser?.username || '',
    };
    await cacheChecklist(instance, template);
    toast.success('Started on this device — it will upload when you\'re back online');
    setShowCreateDialog(false);
    setHeaderData({});
    setSelectedTemplate(null);
    setSelectedProjectId('');
    await fetchInstances();
    setSelectedInstance({ ...instance, _template: template, _offline: true });
    setShowFillForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('auto.iso.deleteThisChecklist'))) return;
    try {
      await fetch(`${API_BASE}/api/checklists/instances/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      toast.success(t('auto.iso.deleted'));
      fetchInstances();
    } catch (e) { toast.error(t('auto.iso.failedToDelete')); }
  };

  const handleExportPDF = async (id, mono = false, withPhotos = false) => {
    toast.info(withPhotos ? 'Generating PDF with photos…' : (mono ? t('auto.iso.generatingBWPdf') : t('auto.iso.generatingPdf')));
    try {
      const params = new URLSearchParams();
      if (mono) params.append('mono', 'true');
      if (withPhotos) params.append('with_photos', 'true');
      const qs = params.toString() ? `?${params.toString()}` : '';
      const resp = await fetch(`${API_BASE}/api/checklists/instances/${id}/pdf${qs}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('PDF generation failed');
      const blob = await resp.blob();
      const disposition = resp.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="(.+)"/);
      const filename = match ? match[1] : `checklist_${id}${mono ? '_BW' : ''}${withPhotos ? '_with_photos' : ''}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = weekFileName(filename);
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('auto.iso.pdfDownloaded'));
    } catch (e) { toast.error(e.message); }
  };

  const handleOpenInstance = async (inst) => {
    const id = typeof inst === 'string' ? inst : inst.id;
    const openFromDevice = async () => {
      const cached = await getCachedChecklist(id).catch(() => null);
      if (!cached) {
        toast.error(t('auto.iso.thisSheetIsNotOnYour'));
        return false;
      }
      setSelectedInstance({ ...cached.instance, _template: cached.template, _cached: true });
      setShowFillForm(true);
      return true;
    };

    // Started offline, or no signal now: the device copy is the only copy.
    if (isLocalId(id) || (typeof navigator !== 'undefined' && navigator.onLine === false)) {
      await openFromDevice();
      return;
    }
    try {
      const resp = await fetch(`${API_BASE}/api/checklists/instances/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Failed to load');
      const full = await resp.json();
      // Also fetch the template for section/item definitions
      const tplResp = await fetch(`${API_BASE}/api/checklists/templates/${full.template_id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (tplResp.ok) full._template = await tplResp.json();

      // A device copy edited more recently than the server's means an
      // autosave never made it up — use it and say so, rather than quietly
      // handing back older ticks.
      const cached = await getCachedChecklist(id).catch(() => null);
      const localStamp = Date.parse(cached?.instance?.updated_at || '') || 0;
      const serverStamp = Date.parse(full.updated_at || full.created_at || '') || 0;
      if (cached && localStamp > serverStamp) {
        setSelectedInstance({ ...cached.instance, _template: full._template || cached.template, _cached: true });
        setShowFillForm(true);
        toast.info(t('auto.iso.restoredYourUnsavedChangesFromThis'));
        return;
      }
      setSelectedInstance(full);
      setShowFillForm(true);
      if (full._template) cacheChecklist(full, full._template).catch(() => {});
    } catch (e) {
      if (!(await openFromDevice())) toast.error(t('auto.iso.failedToLoadChecklist'));
    }
  };

  // Reopen whatever was on screen when the tab was killed (phone call, low
  // memory) instead of dropping the user back on the list.
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    const last = loadSession()?.openSheet;
    if (last?.source === 'iso' && last.id) handleOpenInstance(last.id);
    /* eslint-disable-next-line */
  }, []);

  useEffect(() => {
    patchSession({ openSheet: showFillForm && selectedInstance ? { source: 'iso', id: selectedInstance.id } : null });
  }, [showFillForm, selectedInstance]);

  // The queue drained — the server now has real numbers, so reload.
  useEffect(() => {
    const onChanged = () => { fetchInstances(); };
    window.addEventListener('offline-queue-changed', onChanged);
    return () => window.removeEventListener('offline-queue-changed', onChanged);
  }, [fetchInstances]);

  // Header-data lookup that tolerates template-specific key names
  // (e.g. system_ref, system__equipment_ref, panel_ref,
  // panel__installation_ref) — templates generate keys from labels.
  const headerVal = (h, patterns) => {
    if (!h) return '';
    for (const [k, v] of Object.entries(h)) {
      if (!v) continue;
      if (patterns.some(p => p.test(k))) return String(v);
    }
    return '';
  };
  const SYSTEM_KEYS = [/^system/, /system.*(ref|equip)/, /equipment.*ref/];
  const PANEL_KEYS = [/^panel/, /panel.*(ref|install)/, /installation.*ref/];

  const filteredInstances = instances.filter(inst => {
    const q = searchTerm.toLowerCase();
    const h = inst.header_data || {};
    const matchesSearch = !q || [
      inst.template_name, inst.doc_ref, inst.checklist_number, inst.created_by_name,
      h.project_name, h.panel_ref, h.system_ref, h.machine_id__asset_no, h.client,
      headerVal(h, SYSTEM_KEYS), headerVal(h, PANEL_KEYS),
    ].some(v => String(v || '').toLowerCase().includes(q));
    const matchesStatus = filterStatus === 'all' || inst.status === filterStatus;
    const matchesDiscipline = filterDiscipline === 'all' || inst.discipline === filterDiscipline;
    const matchesOwner =
      filterOwner === 'all' ||
      (filterOwner === 'mine' && inst.created_by === currentUser?.id) ||
      (filterOwner !== 'mine' && filterOwner !== 'all' && inst.created_by === filterOwner);
    return matchesSearch && matchesStatus && matchesDiscipline && matchesOwner && matchesWeek(weekFilter, inst.created_at);
  });

  // Sorting + optional grouping by checklist type (template name).
  const SORTERS = {
    date_desc: (a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')),
    date_asc: (a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')),
    author: (a, b) => String(a.created_by_name || '').localeCompare(String(b.created_by_name || '')),
    type: (a, b) => String(a.template_name || '').localeCompare(String(b.template_name || '')),
    status: (a, b) => String(a.status || '').localeCompare(String(b.status || '')),
    system: (a, b) => String(a.header_data?.system_ref || '').localeCompare(String(b.header_data?.system_ref || '')),
  };
  const sortedInstances = [...filteredInstances].sort(SORTERS[sortBy] || SORTERS.date_desc);
  const groups = groupByType
    ? Object.entries(sortedInstances.reduce((acc, i) => {
      const k = i.template_name || 'Uncategorised';
      (acc[k] = acc[k] || []).push(i);
      return acc;
    }, {})).sort((a, b) => a[0].localeCompare(b[0]))
    : [[null, sortedInstances]];

  // Distinct owners across loaded instances (for the dropdown)
  const owners = (() => {
    const map = new Map();
    instances.forEach(i => {
      if (i.created_by && i.created_by_name) map.set(i.created_by, i.created_by_name);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  })();

  const getProgress = (inst) => {
    if (!inst.sections_data) return 0;
    let total = 0, filled = 0;
    for (const sec of inst.sections_data) {
      for (const item of (sec.items || [])) {
        total++;
        if (item.status || item.value || item.notes) filled++;
      }
    }
    return total ? Math.round((filled / total) * 100) : 0;
  };

  if (showFillForm && selectedInstance) {
    return (
      <ChecklistFillForm
        instance={selectedInstance}
        template={selectedInstance._template}
        token={token}
        projects={projects}
        employees={employees}
        currentUser={currentUser}
        onBack={() => { setShowFillForm(false); setSelectedInstance(null); fetchInstances(); }}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="installation-checklists">
      {(!isOnline || instances.some((i) => i._offline)) && (
        <div
          className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800"
          data-testid="iso-checklists-offline-banner"
        >
          <strong>{isOnline ? t('auto.iso.waitingToSync') : t('auto.iso.workingOffline')}</strong>{' '}
          {isOnline
            ? 'Sheets started on this device are queued and will upload shortly.'
            : 'You can start, fill and sign sheets — they upload as soon as you have signal.'}
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col md:flex-row gap-4 justify-between">
        <div className="flex flex-1 flex-wrap gap-2">
          <div className="relative min-w-[180px] flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder={t('auto.iso.searchChecklists')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="checklist-search"
            />
          </div>
          <WeekFilter value={weekFilter} onChange={setWeekFilter} testId="iso-checklists-week-filter" />          <Select value={filterDiscipline} onValueChange={setFilterDiscipline}>
            <SelectTrigger className="w-[150px] max-w-full">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder={t('auto.iso.discipline')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('auto.iso.allDisciplines')}</SelectItem>
              <SelectItem value="electrical">{t('auto.iso.electrical')}</SelectItem>
              <SelectItem value="mechanical">{t('auto.iso.mechanical')}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px] max-w-full">
              <SelectValue placeholder={t('auto.iso.status')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('auto.iso.allStatus')}</SelectItem>
              <SelectItem value="draft">{t('auto.iso.draft')}</SelectItem>
              <SelectItem value="in_progress">{t('auto.iso.inProgress')}</SelectItem>
              <SelectItem value="completed">{t('auto.iso.completed')}</SelectItem>
              <SelectItem value="conditional_approval">{t('auto.iso.conditional')}</SelectItem>
              <SelectItem value="signed_off">{t('auto.iso.signedOff')}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterOwner} onValueChange={setFilterOwner}>
            <SelectTrigger className="w-[160px] max-w-full" data-testid="filter-owner-select">
              <SelectValue placeholder={t('auto.iso.owner')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('auto.iso.allOwners2')}</SelectItem>
              {currentUser && <SelectItem value="mine">{t('auto.iso.myChecklists')}</SelectItem>}
              {owners.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[150px] max-w-full" data-testid="checklist-sort-select">
              <SelectValue placeholder={t('auto.iso.sort')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date_desc">{t('auto.iso.newestFirst')}</SelectItem>
              <SelectItem value="date_asc">{t('auto.iso.oldestFirst')}</SelectItem>
              <SelectItem value="author">{t('auto.iso.authorAZ')}</SelectItem>
              <SelectItem value="type">{t('auto.iso.typeAZ')}</SelectItem>
              <SelectItem value="status">{t('auto.iso.status')}</SelectItem>
              <SelectItem value="system">{t('auto.iso.systemRef')}</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant={groupByType ? 'default' : 'outline'}
            onClick={() => setGroupByType(v => !v)}
            data-testid="checklist-group-toggle"
          >
            {t('auto.iso.groupByType')}
          </Button>
        </div>
        {can('iso_checklists.create') && (
          <Button onClick={() => setShowCreateDialog(true)} data-testid="new-checklist-btn">
            <Plus className="h-4 w-4 mr-2" />
            {t('auto.iso.newChecklist')}
          </Button>
        )}
      </div>

      {/* Instances List */}
      <div className="grid gap-3">
        {filteredInstances.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-gray-500">
              <ClipboardCheck className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>{t('auto.iso.noChecklistsFound')}</p>
              <p className="text-sm mt-1">{t('auto.iso.createOneFromATemplateTo')}</p>
            </CardContent>
          </Card>
        ) : (
          groups.map(([groupName, groupItems]) => (
          <React.Fragment key={groupName || 'all'}>
          {groupName && (
            <div className="flex items-center gap-2 mt-2 first:mt-0" data-testid={`checklist-group-${groupName}`}>
              <h4 className="text-sm font-semibold text-gray-700">{groupName}</h4>
              <Badge variant="outline">{groupItems.length}</Badge>
              <div className="flex-1 h-px bg-gray-200" />
            </div>
          )}
          {groupItems.map(inst => {
            const statusCfg = STATUS_CONFIG[inst.status] || STATUS_CONFIG.draft;
            const StatusIcon = statusCfg.icon;
            const DisciplineIcon = DISCIPLINE_ICON[inst.discipline] || FileText;
            const progress = getProgress(inst);
            const project = projects.find(p => p.id === inst.project_id);

            return (
              <Card
                key={inst.id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleOpenInstance(inst)}
                data-testid={`checklist-instance-${inst.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    {/* Picture of the machine: people recognise the scissor
                        lift far faster than they read its asset number. */}
                    {inst.asset_photo_id && (
                      <LazyAuthImage
                        src={`${API_BASE}/api/checklists/asset-photo/${inst.asset_id}`}
                        alt={inst.asset_label || 'equipment'}
                        className="block h-16 w-16 shrink-0 overflow-hidden rounded border bg-gray-50"
                        testId={`checklist-asset-thumb-${inst.id}`}
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                        <DisciplineIcon className="h-4 w-4 text-gray-600" />
                        {inst.checklist_number && (
                          <span className="font-mono text-sm font-semibold">{inst.checklist_number}</span>
                        )}
                        <span className="font-mono text-xs text-gray-400">{inst.doc_ref}</span>
                        <Badge className={statusCfg.color}>{statusCfg.label}</Badge>
                        <Badge variant="outline" className="capitalize">{inst.discipline}</Badge>
                        {inst.type === 'report' && <Badge variant="outline" className="text-orange-600 border-orange-300">{t('auto.iso.report')}</Badge>}
                      </div>
                      <h3 className="font-medium">{inst.template_name}</h3>
                      {inst._offline && (
                        <span
                          className="inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800"
                          data-testid={`checklist-pending-sync-${inst.id}`}
                        >
                          On this device — not uploaded yet
                        </span>
                      )}
                      {inst.template_description && (
                        <p className="text-xs text-gray-500" data-testid={`checklist-description-${inst.id}`}>
                          {inst.template_description}
                        </p>
                      )}
                      {(inst.asset_label || inst.asset_make_model) && (
                        <p className="text-xs font-medium text-gray-700" data-testid={`checklist-asset-${inst.id}`}>
                          {[inst.asset_label, inst.asset_make_model].filter(Boolean).join(' · ')}
                        </p>
                      )}
                      <div className="flex items-center gap-x-4 gap-y-1 mt-1 text-xs text-gray-500 flex-wrap">
                        {inst.header_data?.project_name && (
                          <span>Site: {inst.header_data.project_name}</span>
                        )}
                        {headerVal(inst.header_data, SYSTEM_KEYS) && (
                          <span data-testid={`checklist-system-${inst.id}`}>
                            System / Equipment: <span className="font-medium text-gray-700">{headerVal(inst.header_data, SYSTEM_KEYS)}</span>
                          </span>
                        )}
                        {headerVal(inst.header_data, PANEL_KEYS) && (
                          <span data-testid={`checklist-panel-${inst.id}`}>
                            Panel / Installation: <span className="font-medium text-gray-700">{headerVal(inst.header_data, PANEL_KEYS)}</span>
                          </span>
                        )}
                        {inst.header_data?.machine_id__asset_no && (
                          <span data-testid={`checklist-machine-${inst.id}`}>
                            Machine: <span className="font-medium text-gray-700">{inst.header_data.machine_id__asset_no}</span>
                          </span>
                        )}
                        {project && <span>Project: {project.name}</span>}
                        <span>{<DateText value={inst.created_at} />}</span>
                        {inst.created_by_name && (
                          <span
                            className={inst.created_by === currentUser?.id ? 'text-blue-700 font-medium' : ''}
                            data-testid={`checklist-owner-${inst.id}`}
                          >
                            Owner: {inst.created_by_name}
                          </span>
                        )}
                      </div>
                      {/* Progress bar */}
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden max-w-[200px]">
                          <div
                            className={`h-full rounded-full transition-all ${progress === 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-400">{progress}%</span>
                        {/* Sign-off indicator */}
                        {inst.sign_offs?.length > 0 && (() => {
                          const signed = inst.sign_offs.filter(s => s.signed).length;
                          const total = inst.sign_offs.length;
                          return signed === total ? (
                            <span className="text-xs text-green-600 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> {t('auto.iso.signed')}</span>
                          ) : signed > 0 ? (
                            <span className="text-xs text-amber-600">{signed}/{total} signed</span>
                          ) : (inst.status === 'completed' || inst.status === 'conditional_approval') ? (
                            <span className="text-xs text-gray-400">{t('auto.iso.awaitingSignOff')}</span>
                          ) : null;
                        })()}
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost" size="sm"
                            onClick={(e) => e.stopPropagation()}
                            title={t('auto.iso.exportPdf')}
                            data-testid={`export-pdf-${inst.id}`}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                          <div className="px-2 py-1"><DocLangToggle testId={`doc-lang-installationchecklists-0-${inst.id}`} /></div>
                          <DropdownMenuItem
                            onClick={(e) => { e.stopPropagation(); handleExportPDF(inst.id, false, false); }}
                            data-testid={`export-pdf-color-${inst.id}`}
                          >
                            <Download className="h-4 w-4 mr-2" /> {t('auto.iso.colorPdf')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => { e.stopPropagation(); handleExportPDF(inst.id, true, false); }}
                            data-testid={`export-pdf-bw-${inst.id}`}
                          >
                            <Printer className="h-4 w-4 mr-2" /> Print-friendly (B&amp;W)
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => { e.stopPropagation(); handleExportPDF(inst.id, false, true); }}
                            data-testid={`export-pdf-color-with-photos-${inst.id}`}
                          >
                            <Camera className="h-4 w-4 mr-2" /> {t('auto.iso.colorPhotoEvidence')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => { e.stopPropagation(); handleExportPDF(inst.id, true, true); }}
                            data-testid={`export-pdf-bw-with-photos-${inst.id}`}
                          >
                            <Camera className="h-4 w-4 mr-2" /> B&amp;W + Photo Evidence
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      {can('iso_checklists.unlock_signed') && (
                        <Button
                          variant="ghost" size="sm" className="text-amber-600"
                          onClick={(e) => { e.stopPropagation(); setReassignTarget(inst); }}
                          title={t('auto.iso.reassignOwner')}
                          data-testid={`reassign-checklist-${inst.id}`}
                        >
                          <UserCog className="h-4 w-4" />
                        </Button>
                      )}
                      {can('iso_checklists.delete') && (
                        <Button
                          variant="ghost" size="sm" className="text-red-500"
                          onClick={(e) => { e.stopPropagation(); handleDelete(inst.id); }}
                          data-testid={`delete-checklist-${inst.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          </React.Fragment>
          ))
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('auto.iso.newInstallationChecklist')}</DialogTitle>
            <DialogDescription>{t('auto.iso.selectATemplateAndFillIn')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Template Selection */}
            <div>
              <label className="text-sm font-medium">{t('auto.iso.template2')}</label>
              <div className="grid grid-cols-1 gap-2 mt-1">
                {templates.map(tpl => {
                  const DIcon = DISCIPLINE_ICON[tpl.discipline] || FileText;
                  const selected = selectedTemplate?.id === tpl.id;
                  return (
                    <div
                      key={tpl.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-all ${selected ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'hover:border-gray-300'}`}
                      onClick={() => { setSelectedTemplate(tpl); setHeaderData({}); }}
                      data-testid={`template-option-${tpl.doc_ref}`}
                    >
                      <div className="flex items-center gap-2">
                        <DIcon className="h-4 w-4 text-gray-600" />
                        <span className="font-medium text-sm">{tpl.name}</span>
                        <span className="text-xs text-gray-400 font-mono">{tpl.doc_ref}</span>
                        <Badge variant="outline" className="capitalize text-xs">{tpl.discipline}</Badge>
                        <Badge variant="outline" className="text-xs">{tpl.type === 'report' ? t('auto.iso.report') : t('auto.iso.checklist')}</Badge>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {tpl.description ? `${tpl.description} — ` : ''}
                        {tpl.sections?.length} sections, {tpl.sections?.reduce((a, s) => a + (s.items?.length || 0), 0)} items
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Link to Project */}
            <div>
              <label className="text-sm font-medium">{t('auto.iso.linkToProjectOptional')}</label>
              <Select value={selectedProjectId || 'none'} onValueChange={(v) => {
                const projectId = v === 'none' ? '' : v;
                setSelectedProjectId(projectId);
                if (projectId) fetchProjectAutoFill(projectId, selectedTemplate?.discipline);
              }}>
                <SelectTrigger><SelectValue placeholder={t('auto.iso.selectAProject')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('auto.iso.none')}</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Header Fields from selected template */}
            {selectedTemplate && (
              <div className="space-y-3 border-t pt-3">
                <p className="text-sm font-medium text-gray-700">{t('auto.iso.projectInformation')}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {selectedTemplate.header_fields?.map(field => (
                    <div key={field.key}>
                      <label className="text-xs font-medium text-gray-500">
                        {field.label} {field.required && '*'}
                      </label>
                      <Input
                        type={field.type === 'date' ? 'date' : 'text'}
                        value={headerData[field.key] || ''}
                        onChange={(e) => setHeaderData(prev => ({ ...prev, [field.key]: e.target.value  }))}
                        placeholder={field.placeholder || ''}
                        className="mt-0.5"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} data-testid="dialog-cancel-btn">{t('auto.iso.cancel')}</Button>
            <Button onClick={handleCreateInstance} disabled={isLoading || !selectedTemplate} data-testid="dialog-createinstance-btn">
              {isLoading ? t('auto.iso.creating') : t('auto.iso.createStartFilling')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReassignOwnerDialog
        open={!!reassignTarget}
        onOpenChange={(o) => { if (!o) setReassignTarget(null); }}
        instance={reassignTarget}
        kind="checklist"
        onReassigned={() => fetchInstances()}
      />
    </div>
  );
};

export default InstallationChecklists;
